/**
 * Maintenance Windows Page
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateTime } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2, Wrench, Bell } from 'lucide-react';

interface MaintenanceWindow {
  id: string;
  applicationId: string;
  applicationName: string;
  title: string;
  description: string | null;
  scheduledStart: string;
  scheduledEnd: string;
  status: string;
  notifyBefore: number | null;
  notificationSent: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Application {
  id: string;
  name: string;
}

const statusColors: Record<string, string> = {
  scheduled: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-600',
};

const statusLabels: Record<string, string> = {
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export function MaintenanceWindowsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [appFilter, setAppFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWindow, setEditingWindow] = useState<MaintenanceWindow | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingWindow, setDeletingWindow] = useState<MaintenanceWindow | null>(null);

  // Form state
  const [formApplicationId, setFormApplicationId] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formScheduledStart, setFormScheduledStart] = useState('');
  const [formScheduledEnd, setFormScheduledEnd] = useState('');
  const [formStatus, setFormStatus] = useState('scheduled');
  const [formNotifyBefore, setFormNotifyBefore] = useState('1440');

  // Fetch applications
  const { data: appsData } = useQuery({
    queryKey: ['finance-applications'],
    queryFn: async () => {
      const result = await api.getFinanceApplications();
      return result;
    },
  });

  const applications = (appsData?.data || []) as Application[];

  // Fetch maintenance windows
  const { data: windowsData, isLoading } = useQuery({
    queryKey: ['maintenance-windows', { appFilter, statusFilter, page }],
    queryFn: () =>
      api.getMaintenanceWindows({
        applicationId: appFilter || undefined,
        status: statusFilter || undefined,
        page,
        limit: 20,
      }),
  });

  const windows = (windowsData?.data || []) as MaintenanceWindow[];
  const pagination = windowsData?.pagination;

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createMaintenanceWindow(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-windows'] });
      closeDialog();
      toast({
        title: 'Maintenance scheduled',
        description: 'Maintenance window has been created.',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to create maintenance window.',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.updateMaintenanceWindow(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-windows'] });
      closeDialog();
      toast({
        title: 'Maintenance updated',
        description: 'Maintenance window has been updated.',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update maintenance window.',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteMaintenanceWindow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-windows'] });
      setDeleteDialogOpen(false);
      setDeletingWindow(null);
      toast({
        title: 'Maintenance deleted',
        description: 'Maintenance window has been deleted.',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to delete maintenance window.',
        variant: 'destructive',
      });
    },
  });

  function openCreateDialog() {
    setEditingWindow(null);
    setFormApplicationId('');
    setFormTitle('');
    setFormDescription('');
    setFormScheduledStart('');
    setFormScheduledEnd('');
    setFormStatus('scheduled');
    setFormNotifyBefore('1440');
    setDialogOpen(true);
  }

  function openEditDialog(mw: MaintenanceWindow) {
    setEditingWindow(mw);
    setFormApplicationId(mw.applicationId);
    setFormTitle(mw.title);
    setFormDescription(mw.description || '');
    setFormScheduledStart(
      mw.scheduledStart ? mw.scheduledStart.slice(0, 16) : ''
    );
    setFormScheduledEnd(
      mw.scheduledEnd ? mw.scheduledEnd.slice(0, 16) : ''
    );
    setFormStatus(mw.status);
    setFormNotifyBefore(mw.notifyBefore != null ? mw.notifyBefore.toString() : '1440');
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditingWindow(null);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const payload: Record<string, unknown> = {
      applicationId: formApplicationId,
      title: formTitle,
      description: formDescription || null,
      scheduledStart: formScheduledStart ? new Date(formScheduledStart).toISOString() : null,
      scheduledEnd: formScheduledEnd ? new Date(formScheduledEnd).toISOString() : null,
      notifyBefore: formNotifyBefore ? parseInt(formNotifyBefore) : 1440,
    };

    if (editingWindow) {
      payload.status = formStatus;
      updateMutation.mutate({ id: editingWindow.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Maintenance Windows</h1>
          <p className="text-muted-foreground">Schedule and manage planned maintenance</p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="h-4 w-4 mr-2" />
          Schedule Maintenance
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="w-64">
          <Select
            value={appFilter}
            onValueChange={(v) => {
              setAppFilter(v === 'all' ? '' : v);
              setPage(1);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="All Applications" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Applications</SelectItem>
              {applications.map((app) => (
                <SelectItem key={app.id} value={app.id}>
                  {app.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-48">
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v === 'all' ? '' : v);
              setPage(1);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : windows.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Wrench className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium mb-2">No maintenance windows</h3>
            <p className="text-sm text-muted-foreground">
              No maintenance windows have been scheduled yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Application</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Scheduled Start</TableHead>
                <TableHead>Scheduled End</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Notification</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {windows.map((mw) => (
                <TableRow key={mw.id}>
                  <TableCell className="font-medium">
                    {mw.applicationName || 'Unknown'}
                  </TableCell>
                  <TableCell>{mw.title}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDateTime(mw.scheduledStart)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDateTime(mw.scheduledEnd)}
                  </TableCell>
                  <TableCell>
                    <Badge className={statusColors[mw.status] || ''}>
                      {statusLabels[mw.status] || mw.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {mw.notificationSent ? (
                      <Badge className="bg-green-100 text-green-800">
                        <Bell className="h-3 w-3 mr-1" />
                        Sent
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">
                        <Bell className="h-3 w-3 mr-1" />
                        Pending
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => openEditDialog(mw)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => {
                          setDeletingWindow(mw);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {pagination.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page === pagination.totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent key={editingWindow?.id || 'new'} className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingWindow ? 'Edit Maintenance Window' : 'Schedule Maintenance'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Application *</Label>
              <Select value={formApplicationId} onValueChange={setFormApplicationId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select application" />
                </SelectTrigger>
                <SelectContent>
                  {applications.map((app) => (
                    <SelectItem key={app.id} value={app.id}>
                      {app.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Title *</Label>
              <Input
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="e.g. Database upgrade"
                required
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Describe the maintenance work..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Scheduled Start *</Label>
                <Input
                  type="datetime-local"
                  value={formScheduledStart}
                  onChange={(e) => setFormScheduledStart(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Scheduled End *</Label>
                <Input
                  type="datetime-local"
                  value={formScheduledEnd}
                  onChange={(e) => setFormScheduledEnd(e.target.value)}
                  required
                />
              </div>
            </div>

            {editingWindow && (
              <div>
                <Label>Status</Label>
                <Select value={formStatus} onValueChange={setFormStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Notify Before (minutes)</Label>
              <Input
                type="number"
                min="0"
                value={formNotifyBefore}
                onChange={(e) => setFormNotifyBefore(e.target.value)}
                placeholder="1440"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Default: 1440 minutes (24 hours)
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSaving || !formTitle || !formApplicationId || !formScheduledStart || !formScheduledEnd}
              >
                {isSaving
                  ? 'Saving...'
                  : editingWindow
                    ? 'Update Window'
                    : 'Schedule Maintenance'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Maintenance Window</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <strong>{deletingWindow?.title}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                deletingWindow && deleteMutation.mutate(deletingWindow.id)
              }
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Window'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
